export const layers = [
  {
    id: 1,
    title: "Foundation",
    description: "The basics every Indian must know before investing a single rupee",
    lessons: [
      {
        id: 1,
        title: "What is money really?",
        description: "Why money has value and how it actually works",
        duration: 6,
        content: {
          intro: "We use money every single day — but most of us have never stopped to ask: what actually is it? Why does a piece of paper let you buy rice, pay rent, and get a haircut?",
          sections: [
            {
              heading: "Money is trust",
              body: "Money has no real value on its own. A ₹500 note is just paper. But everyone in India agrees it is worth ₹500 — so it is. Money works because of collective trust. The moment that trust breaks — like in hyperinflation — money becomes worthless."
            },
            {
              heading: "Money replaced barter",
              body: "Before money, people traded directly. A farmer gave wheat to get shoes. The problem — what if the shoemaker didn't need wheat? Money solved this. It became a universal medium everyone would accept."
            },
            {
              heading: "Three things money does",
              body: "Money serves three purposes: it is a medium of exchange (you use it to buy things), a store of value (you save it for later), and a unit of account (you use it to measure the price of things)."
            },
            {
              heading: "The real lesson",
              body: "Money is a tool. Like any tool, it works well when you understand it and badly when you don't. Most financial mistakes happen because people treat money as a goal instead of a tool to reach their actual goals."
            }
          ],
          example: "Think about this: ₹1,000 means nothing to a person on a deserted island. But that same ₹1,000 in Mumbai can feed a family for a week. Money only has value within a society that agrees it does.",
          keyTakeaway: "Money is not wealth. Money is a tool that helps you build wealth. Understanding this one distinction changes how you think about earning, saving, and spending."
        },
        quiz: [
          {
            id: 1,
            question: "Why does a ₹500 note have value?",
            options: [
              "Because it is made of special paper",
              "Because the government printed it",
              "Because everyone agrees it is worth ₹500",
              "Because it has a serial number"
            ],
            correct: 2,
            explanation: "Money works because of collective trust and agreement. The paper itself has no intrinsic value — its value comes from society's shared belief in it."
          },
          {
            id: 2,
            question: "What problem did money solve?",
            options: [
              "People had too much gold",
              "Barter did not work when two people didn't need each other's goods",
              "Governments needed to control trade",
              "People needed a way to save"
            ],
            correct: 1,
            explanation: "Money solved the 'double coincidence of wants' problem in barter — you no longer needed to find someone who had what you wanted AND wanted what you had."
          },
          {
            id: 3,
            question: "Which of these is the right way to think about money?",
            options: [
              "Money is the goal of life",
              "Money is dangerous and should be avoided",
              "Money is a tool to help you reach your real goals",
              "Money is only for rich people"
            ],
            correct: 2,
            explanation: "Money is a tool — nothing more, nothing less. People who treat it as a goal often feel empty even when they have a lot of it. People who treat it as a tool use it to build the life they actually want."
          }
        ]
      },
      {
        id: 2,
        title: "Time value of money",
        description: "Why ₹1,000 today is worth more than ₹1,000 tomorrow",
        duration: 7,
        content: {
          intro: "Here is a simple question: would you rather have ₹10,000 today or ₹10,000 one year from now? Most people instinctively say today — and they are right. But do you know exactly why?",
          sections: [
            {
              heading: "Money today is worth more",
              body: "₹10,000 today can be invested and grow. In one year at 7% interest, it becomes ₹10,700. If you wait and take ₹10,000 in a year, you lose that ₹700. This is the time value of money — money available now is worth more than the same amount later."
            },
            {
              heading: "Compound interest — the most powerful idea in finance",
              body: "When your money earns interest, and that interest also earns interest, it compounds. ₹10,000 at 12% for 10 years does not become ₹22,000 (simple interest). It becomes ₹31,058. The longer you wait, the more dramatic the difference."
            },
            {
              heading: "Starting early changes everything",
              body: "Arjun starts investing ₹1,000/month at age 22. Priya starts the same at age 32. Both invest until age 60 at 12% returns. Arjun gets ₹3.5 crore. Priya gets ₹1.1 crore. The only difference is 10 years of starting early."
            },
            {
              heading: "The cost of waiting",
              body: "Every year you delay starting to invest costs you more than you think. Not just the returns you miss — but the compounding on those returns, and the compounding on that compounding. Waiting is expensive."
            }
          ],
          example: "If your grandfather had invested ₹1,000 in 1980 in a good fund averaging 15% returns, it would be worth over ₹1 crore today. The money did not change. Time did the work.",
          keyTakeaway: "Start investing as early as possible, even with a tiny amount. Time in the market is more important than the amount you invest."
        },
        quiz: [
          {
            id: 1,
            question: "Would you rather have ₹50,000 today or ₹50,000 two years from now?",
            options: [
              "Two years from now — more time to plan",
              "Today — because I can invest it and it will grow",
              "It makes no difference",
              "Two years from now — it will be worth more"
            ],
            correct: 1,
            explanation: "₹50,000 today invested at 10% becomes ₹60,500 in two years. Taking it later means losing that growth. Money now is always worth more than money later."
          },
          {
            id: 2,
            question: "Arjun starts investing at 22. Priya starts at 32. Same amount, same returns. Who has more at 60?",
            options: [
              "Priya — she was more careful",
              "They have the same — same amount invested",
              "Arjun — because compound interest works longer",
              "Depends on what they invested in"
            ],
            correct: 2,
            explanation: "Arjun ends up with over 3x more than Priya — not because he invested more, but because compound interest had 10 extra years to work. Starting early is the single most powerful financial decision."
          },
          {
            id: 3,
            question: "What is compound interest?",
            options: [
              "Interest paid by a bank on a loan",
              "Interest earned on your principal AND on the interest already earned",
              "A type of fixed deposit",
              "Tax on your savings"
            ],
            correct: 1,
            explanation: "Compound interest means your interest earns interest. This creates exponential growth over time — which is why Albert Einstein reportedly called it the eighth wonder of the world."
          }
        ]
      },
      {
        id: 3,
        title: "Inflation — your silent enemy",
        description: "How inflation quietly erodes your savings every year",
        duration: 7,
        content: {
          intro: "You have ₹1 lakh in your savings account. You feel secure. But here is something most people don't realize — every year, that money is quietly losing its power to buy things. This is inflation.",
          sections: [
            {
              heading: "What is inflation?",
              body: "Inflation is the rate at which prices rise over time. If inflation is 6%, something that costs ₹100 today will cost ₹106 next year. Your money buys less and less over time — even if the number in your account stays the same."
            },
            {
              heading: "A real Indian example",
              body: "In 2010, a plate of idli in Hyderabad cost ₹15. Today the same plate costs ₹50. The idli did not change. Money lost its purchasing power. If you saved ₹15 in 2010 to buy an idli later, today that ₹15 is not enough. Inflation ate the difference."
            },
            {
              heading: "Why your savings account is not enough",
              body: "A savings account gives you 3–4% interest. If inflation is 6%, your real return is negative. You are actually losing purchasing power every year even though your account balance is going up. This is why keeping all your money in a savings account is dangerous long-term."
            },
            {
              heading: "What beats inflation?",
              body: "To protect your money you need returns higher than inflation. Equity mutual funds have historically returned 12–15% annually in India — well above inflation. Fixed deposits at 7% barely beat 6% inflation. Savings accounts don't beat it at all."
            }
          ],
          example: "₹1 lakh in a savings account at 3.5% for 10 years = ₹1.41 lakh. But with 6% inflation, you need ₹1.79 lakh just to have the same purchasing power. You lost ₹38,000 in real value while thinking you were saving.",
          keyTakeaway: "Saving money is not enough. You must invest it in something that grows faster than inflation — otherwise your money is silently shrinking every single year."
        },
        quiz: [
          {
            id: 1,
            question: "If inflation is 6% and your savings account gives 3.5% interest, what is happening to your money?",
            options: [
              "It is growing at 3.5%",
              "It is growing at 9.5%",
              "It is losing purchasing power every year",
              "Nothing — the amount in the account stays the same"
            ],
            correct: 2,
            explanation: "When inflation (6%) is higher than your returns (3.5%), you have a negative real return of -2.5%. Your account balance goes up but what it can buy goes down."
          },
          {
            id: 2,
            question: "A plate of idli cost ₹15 in 2010. Today it costs ₹50. What caused this?",
            options: [
              "Idli became more popular",
              "Inflation — money lost purchasing power over time",
              "Restaurant owners became greedy",
              "Ingredients became rarer"
            ],
            correct: 1,
            explanation: "This is inflation in action. The idli did not change. But money lost its purchasing power over the years — so you need more of it to buy the same thing."
          },
          {
            id: 3,
            question: "Which option is most likely to beat 6% inflation over 10 years?",
            options: [
              "Savings account at 3.5%",
              "Cash under the mattress",
              "Fixed deposit at 7%",
              "Nifty 50 index fund averaging 12%"
            ],
            correct: 3,
            explanation: "Only returns significantly above inflation protect your wealth. A Nifty 50 index fund has historically returned 12–15% annually — well above the 6% inflation rate. FD barely beats it and savings accounts don't at all."
          }
        ]
      },
      {
        id: 4,
        title: "Risk and return",
        description: "Why higher returns always mean higher risk — no exceptions",
        duration: 6,
        content: {
          intro: "Anyone who promises you high returns with zero risk is lying to you. This is the single most important rule in all of finance — and understanding it will protect you from losing money your entire life.",
          sections: [
            {
              heading: "The golden rule",
              body: "Higher potential return always comes with higher risk. Always. No exceptions. This is not a guideline — it is a law of finance. The moment someone offers you guaranteed high returns, walk away."
            },
            {
              heading: "The risk-return spectrum",
              body: "At one end — savings accounts. Zero risk, 3.5% return. At the other end — individual stocks. High risk, potentially 0% to 50%+ return. Everything sits somewhere on this spectrum. You choose where based on your situation."
            },
            {
              heading: "Risk is not always bad",
              body: "Young people with steady income can take more risk because they have time to recover from losses. A 60-year-old retiree should take less risk because they need their money now. Risk tolerance depends on your age, income, and goals."
            },
            {
              heading: "Diversification reduces risk",
              body: "If you put all your money in one stock and it crashes, you lose everything. If you spread across 50 stocks (like a Nifty 50 index fund), one stock crashing barely affects you. This is diversification — the only free lunch in investing."
            }
          ],
          example: "Ponzi schemes and chit fund scams always promise high guaranteed returns. They work for a while — then collapse. If something sounds too good to be true in finance, it always is.",
          keyTakeaway: "Match your investments to your risk tolerance. Never chase high returns without understanding the risk. And never trust anyone who promises guaranteed returns above FD rates."
        },
        quiz: [
          {
            id: 1,
            question: "Someone offers you 5% guaranteed monthly returns on your investment. What should you do?",
            options: [
              "Invest immediately — great returns",
              "Invest half to be safe",
              "Walk away — this is almost certainly a scam",
              "Ask for more details first"
            ],
            correct: 2,
            explanation: "5% monthly = 60% annual guaranteed return. No legitimate investment can guarantee this. This is a classic sign of a Ponzi scheme or fraud. Walk away immediately."
          },
          {
            id: 2,
            question: "Who can generally afford to take more investment risk?",
            options: [
              "A 60-year-old who just retired",
              "A 25-year-old with stable monthly income",
              "Someone who needs the money in 6 months",
              "A person with no emergency fund"
            ],
            correct: 1,
            explanation: "A young person with stable income can take more risk because they have time to recover from losses and decades for investments to grow. Risk tolerance depends on age, income stability, and time horizon."
          },
          {
            id: 3,
            question: "What is diversification?",
            options: [
              "Investing in multiple countries",
              "Spreading investments across many assets to reduce risk",
              "Changing investments frequently",
              "Investing in only safe assets"
            ],
            correct: 1,
            explanation: "Diversification means not putting all your eggs in one basket. By spreading investments across many assets, a loss in one does not destroy your whole portfolio."
          }
        ]
      },
      {
        id: 5,
        title: "Emergency fund — before anything else",
        description: "The one thing that stops you needing a loan in a crisis",
        duration: 5,
        content: {
          intro: "Before you invest a single rupee — before SIP, before stocks, before anything — you need an emergency fund. This is not optional. It is the foundation everything else stands on.",
          sections: [
            {
              heading: "What is an emergency fund?",
              body: "An emergency fund is 3 to 6 months of your living expenses, kept in a liquid account you can access immediately. It is not for planned expenses. It is only for genuine emergencies — job loss, medical crisis, urgent repair."
            },
            {
              heading: "Why it matters more than investing",
              body: "Without an emergency fund, any crisis forces you to either break your investments at the wrong time or take a high-interest loan. Both destroy wealth. An emergency fund is what keeps one bad month from becoming a financial disaster."
            },
            {
              heading: "How much do you need?",
              body: "Calculate your monthly expenses — rent, food, bills, transport, EMIs. Multiply by 3 (minimum) or 6 (ideal). If your monthly expenses are ₹15,000 — you need ₹45,000 to ₹90,000 in your emergency fund before investing."
            },
            {
              heading: "Where to keep it",
              body: "Keep your emergency fund only in a savings account or liquid mutual fund. Not in stocks. Not in FD with lock-in. It must be accessible within 24 hours. Returns do not matter here — accessibility does."
            }
          ],
          example: "Suresh invested all his savings in mutual funds. When he lost his job, markets were down 30%. He had to sell his investments at a loss to survive. If he had 3 months of expenses in a savings account, he could have waited for the market to recover.",
          keyTakeaway: "Build your emergency fund before anything else. 3 months of expenses minimum, in a savings account. Only then start investing."
        },
        quiz: [
          {
            id: 1,
            question: "Your monthly expenses are ₹20,000. What is the minimum emergency fund you should have?",
            options: [
              "₹20,000 — one month is enough",
              "₹60,000 — three months of expenses",
              "₹5,000 — just for small emergencies",
              "₹2,00,000 — as much as possible"
            ],
            correct: 1,
            explanation: "The minimum emergency fund is 3 months of expenses. For ₹20,000 monthly expenses, that is ₹60,000. Ideally build it up to 6 months (₹1,20,000) for more security."
          },
          {
            id: 2,
            question: "Where should you keep your emergency fund?",
            options: [
              "In stocks for higher returns",
              "In gold for safety",
              "In a savings account or liquid mutual fund",
              "In a fixed deposit with 1 year lock-in"
            ],
            correct: 2,
            explanation: "Your emergency fund must be instantly accessible — within 24 hours. A savings account or liquid mutual fund gives you this. FDs with lock-in and stocks can't be accessed immediately without penalty or loss."
          },
          {
            id: 3,
            question: "You have ₹50,000 saved. Should you invest it or build your emergency fund first?",
            options: [
              "Invest it all — better returns",
              "Split 50-50 between investing and emergency fund",
              "Build the emergency fund first, then invest",
              "Spend it — you can always save later"
            ],
            correct: 2,
            explanation: "Always build your emergency fund first. Without it, any crisis will force you to break investments or take loans. Once your emergency fund is set, every rupee beyond that can be invested."
          }
        ]
      },
      {
        id: 6,
        title: "Good debt vs bad debt",
        description: "Not all borrowing is wrong — here is how to tell the difference",
        duration: 6,
        content: {
          intro: "Most people think all debt is bad. This is wrong. The right kind of debt, taken for the right reason, can actually help you build wealth. The wrong kind destroys it. Knowing the difference is critical.",
          sections: [
            {
              heading: "Good debt — borrows to build",
              body: "Good debt is used to acquire something that grows in value or generates income. A home loan to buy a house — the house may appreciate. An education loan for a degree that increases your earning — your income grows. The cost of the debt is less than the value it creates."
            },
            {
              heading: "Bad debt — borrows to consume",
              body: "Bad debt is used for things that lose value or disappear. A loan to buy a phone. A credit card balance for clothes or restaurants. An instant loan app for daily expenses. You are paying interest for something that gives you no financial return."
            },
            {
              heading: "The worst debt — instant loan apps",
              body: "Instant loan apps charge 60–120% annual interest. You borrow ₹10,000, receive ₹7,000 after fees, and must repay in 7 days. Most people cannot — so they borrow again to repay. The debt doubles and triples. This is the most dangerous financial trap in India right now."
            },
            {
              heading: "Credit cards — good tool or bad debt?",
              body: "A credit card used correctly is free money — you get 30–45 days interest-free. Pay the full balance every month and it costs nothing. Pay only the minimum — and you pay 36–42% annual interest, among the highest legal rates in India."
            }
          ],
          example: "Raj took a ₹20 lakh home loan at 9% to buy a flat. The flat is now worth ₹35 lakh. His debt built wealth. Priya took a ₹50,000 personal loan at 24% for a vacation. She paid ₹65,000 back and has nothing to show for it. Same action (borrowing), completely different outcomes.",
          keyTakeaway: "Ask one question before any loan: will this debt help me create something worth more than what I borrowed? If yes — it may be good debt. If no — avoid it."
        },
        quiz: [
          {
            id: 1,
            question: "Which of these is an example of good debt?",
            options: [
              "Borrowing ₹30,000 from a loan app for a new phone",
              "Credit card debt for restaurant bills",
              "A home loan to buy a property that may appreciate",
              "A personal loan for a vacation"
            ],
            correct: 2,
            explanation: "A home loan is good debt because the asset (the house) may appreciate in value over time. The debt is used to acquire something with potential financial return."
          },
          {
            id: 2,
            question: "You have a ₹15,000 credit card bill. You pay only the ₹500 minimum. What happens?",
            options: [
              "The bank is happy and you save money",
              "You pay 36–42% annual interest on the remaining balance",
              "Nothing — minimum payment is enough",
              "Your credit score improves"
            ],
            correct: 1,
            explanation: "Paying only the minimum on a credit card means the remaining balance attracts 36–42% annual interest — among the highest legal rates in India. A ₹15,000 balance can grow dramatically over months."
          },
          {
            id: 3,
            question: "An instant loan app offers you ₹10,000 in 2 minutes. What should you check first?",
            options: [
              "How quickly you can get the money",
              "The annual interest rate and all fees charged",
              "If your friends have used it",
              "How good the app looks"
            ],
            correct: 1,
            explanation: "Instant loan apps often charge 60–120% annual interest plus heavy fees. Always check the total cost of borrowing — not just the convenience. These are the most dangerous financial products in India right now."
          }
        ]
      }
    ]
  }
];

export const mistakes = [
  {
    id: 1,
    title: "Instant loan apps",
    tag: "Debt trap",
    duration: 5,
    description: "You borrow ₹10,000 but only ₹7,000 reaches your hand. Fees eat the rest — and the 7-day repayment cycle traps you in growing debt.",
    warning: "Annual interest: 60–120%",
    content: {
      intro: "There are over 200 instant loan apps operating in India. They show up as ads on YouTube, Instagram, between cricket matches. '₹50,000 in 10 minutes.' 'No documents needed.' 'Just your Aadhaar.' They are designed to look helpful. Most of them are designed to trap you.",
      sections: [
        {
          heading: "How they actually work",
          body: "You apply and get approved in minutes. But you receive ₹7,000–8,000 for a ₹10,000 loan — the remaining ₹2,000–3,000 is taken upfront as 'processing fees.' You still owe the full ₹10,000. The repayment window is 7–30 days."
        },
        {
          heading: "The debt loop",
          body: "Most people cannot repay in 7 days. So they borrow from another app to repay the first — each cycle takes a fresh processing fee. The debt compounds. People end up paying ₹30,000–40,000 to settle what started as a ₹10,000 loan."
        },
        {
          heading: "What happens if you don't pay",
          body: "Many illegal loan apps access your phone contacts and send threatening messages to your family, employer, and friends. Several suicides have been linked to this harassment. RBI has banned hundreds of these apps — but new ones keep appearing."
        },
        {
          heading: "How to tell legitimate from predatory",
          body: "RBI-registered NBFCs are legal and must show APR clearly. If an app does not display the annual interest rate, asks for access to your contacts, or promises money in '2 minutes with no checks' — it is predatory. Walk away."
        }
      ],
      example: "Ramesh borrowed ₹8,000 from a loan app for groceries. He received ₹6,200 after fees. He couldn't repay in 7 days, so borrowed from another app to repay the first. Over 3 months he had borrowed from 6 apps and owed ₹54,000. His salary was ₹18,000.",
      keyTakeaway: "Never use an instant loan app for daily expenses. If you need emergency cash, ask family first, then a scheduled bank. If you regularly borrow for expenses, the real problem is your budget — not your access to loans."
    },
    quiz: [
      {
        id: 1,
        question: "You apply for a ₹10,000 loan and receive ₹7,200. What happened to the remaining ₹2,800?",
        options: [
          "It will be sent separately later",
          "It was taken upfront as processing fees — you still owe ₹10,000",
          "The app kept it as a security deposit",
          "This is normal — all banks do this"
        ],
        correct: 1,
        explanation: "Instant loan apps deduct processing fees, insurance charges, and other costs upfront. You receive less than you borrowed but owe the full amount. This hides the true cost of the loan."
      },
      {
        id: 2,
        question: "An instant loan app asks permission to access your phone contacts. What should you do?",
        options: [
          "Allow it — they need it to verify your identity",
          "Allow it — it helps them process the loan faster",
          "Deny permission and avoid this app entirely",
          "Allow read-only access"
        ],
        correct: 2,
        explanation: "Legitimate lenders do not need your contacts. Apps that request contact access use it to harass your family and employer if you miss a payment. This is a clear warning sign of a predatory lender."
      },
      {
        id: 3,
        question: "What is the safest first step if you genuinely need emergency cash?",
        options: [
          "Download the fastest loan app you can find",
          "Ask a family member or friend first, then approach a scheduled bank",
          "Use a credit card cash advance",
          "Sell your phone"
        ],
        correct: 1,
        explanation: "The safest emergency cash comes from family/friends (zero interest) or scheduled banks (regulated, lower rates). Instant loan apps should be the absolute last resort — and only RBI-registered ones at that."
      }
    ]
  },
  {
    id: 2,
    title: "Telegram stock tip groups",
    tag: "Investment scam",
    duration: 4,
    description: "They recommend a stock, you buy, the price rises briefly, then they sell and it crashes. You lose everything. This is illegal pump-and-dump.",
    warning: "89% of followers lose money",
    content: {
      intro: "You get added to a Telegram group. There are 40,000 members. The admin posts a 'research report' recommending a small company called ABC Ltd. The stock goes up 15% in two days. The next tip also works. You start trusting the group. The third tip destroys you.",
      sections: [
        {
          heading: "What is pump and dump",
          body: "A pump and dump works like this: the operator buys a cheap, thinly-traded stock. They hype it to thousands of people. Everyone buys, the price rises. The operator sells at a profit. The price crashes. Everyone who bought based on the tip loses money."
        },
        {
          heading: "Why the first few tips seem to work",
          body: "This is the hook. The operator gives 2–3 correct or lucky tips first to build trust. Once the group is large enough and trusts them, they execute the actual scam. You think you have found a reliable source. You have not."
        },
        {
          heading: "It is illegal",
          body: "SEBI classifies this as market manipulation. People have been prosecuted and fined crores. But operators often work from outside India or use fake identities. New groups appear as quickly as old ones are banned. WhatsApp groups work the same way."
        },
        {
          heading: "The alternative: boring and effective",
          body: "Instead of chasing 'alpha' through tips, invest in a Nifty 50 index fund. You get market returns — which beat 80% of active strategies over 10 years — without any risk of being scammed. No tips needed."
        }
      ],
      example: "Priya joined a Telegram group after her colleague made ₹12,000 on a tip. She invested ₹30,000 on the third recommendation. The stock rose 8% then crashed 60% in one day. She lost ₹18,000. The admin disappeared. The group was deleted.",
      keyTakeaway: "No one gives free stock tips out of generosity. If someone is sharing 'research' for free in a group, they either want you to buy something they already own, or they are selling a paid service. Neither is worth your money or your trust."
    },
    quiz: [
      {
        id: 1,
        question: "The first 2 tips in a Telegram group worked perfectly. What should you conclude?",
        options: [
          "The admin is genuinely good at picking stocks",
          "The group is legitimate — keep following",
          "The operator may be building trust before executing a scam",
          "It is safe to invest more now"
        ],
        correct: 2,
        explanation: "Early correct tips are a deliberate tactic. The operator gives good picks first to build a large, trusting audience. The actual scam — recommending a stock they already own — comes later. The bigger the trust, the bigger the eventual loss."
      },
      {
        id: 2,
        question: "What exactly is a 'pump and dump'?",
        options: [
          "A government scheme to boost small companies",
          "Buying a stock, hyping it to others, then selling when the price rises — leaving buyers with losses",
          "A mutual fund strategy for volatile markets",
          "Short-selling a stock to make profit"
        ],
        correct: 1,
        explanation: "Pump and dump: the operator buys cheap, hypes it publicly, price rises as others buy, operator sells at a profit, price crashes. Everyone who bought based on the hype loses money. SEBI treats this as market manipulation."
      },
      {
        id: 3,
        question: "What is the reliable alternative to stock tips for long-term wealth building?",
        options: [
          "Find a better Telegram group with paid tips",
          "Follow SEBI-registered advisors on social media",
          "A Nifty 50 index fund held for years",
          "Trade intraday yourself"
        ],
        correct: 2,
        explanation: "A Nifty 50 index fund has historically returned 12% annually in India and beats 80% of active strategies over 10 years. No tips, no scams, no stress. Just consistent long-term compounding."
      }
    ]
  },
  {
    id: 3,
    title: "Mixing insurance with investment",
    tag: "Insurance mistake",
    duration: 6,
    description: "ULIPs and endowment plans give you weak insurance and poor returns. Most people don't realise they've been sold a product that serves the agent more than them.",
    warning: "Returns: 4–6% after charges vs 12% from index funds",
    content: {
      intro: "An LIC agent visits your home. He explains a plan where you pay ₹50,000 per year and after 20 years get ₹15 lakhs back — plus your family is covered if something happens to you. It sounds great. It is usually not.",
      sections: [
        {
          heading: "What these products are",
          body: "ULIPs (Unit Linked Insurance Plans) and endowment/money-back policies combine life insurance with investment. They are pushed aggressively in India because agents earn high commissions — often 20–40% of your first year's premium. The product is designed around the commission, not your needs."
        },
        {
          heading: "The insurance is dangerously inadequate",
          body: "A plan with ₹50,000 annual premium typically provides ₹5–10 lakh life cover. A term insurance plan for ₹10,000 per year gives ₹1 crore cover for the same person. You are paying 5x more for 10x less protection. If you actually died, your family would be in trouble."
        },
        {
          heading: "The returns are poor",
          body: "After agent commissions, fund management charges, mortality charges, and administration fees, ULIPs and endowment plans often return 4–6% annually. A simple Nifty 50 index fund averages 12%. Over 20 years, this difference becomes several times your total investment."
        },
        {
          heading: "The right approach",
          body: "Buy term insurance separately — pure protection, no investment, very cheap. Invest the rest in mutual funds — pure growth, no insurance charges. This 'buy term and invest the rest' approach is the standard advice from every serious financial planner in India."
        }
      ],
      example: "Anil paid ₹50,000 per year for 20 years in an LIC endowment plan and received ₹15 lakh at maturity. If he had bought a ₹10,000 term plan and invested ₹40,000 per year in an index fund at 12%, he would have received ₹32 lakh — and had 10x more life insurance coverage throughout.",
      keyTakeaway: "Never buy a product that mixes insurance and investment. Buy term insurance for protection. Invest in mutual funds for growth. Keep them completely separate — this single change can double your wealth over 20 years."
    },
    quiz: [
      {
        id: 1,
        question: "An endowment plan gives ₹5 lakh life cover for ₹50,000/year premium. What does a term plan give for the same person?",
        options: [
          "About the same — ₹5–10 lakh cover",
          "₹25–50 lakh cover",
          "₹1 crore cover for around ₹10,000/year",
          "Term plans don't give life cover"
        ],
        correct: 2,
        explanation: "A 30-year-old can get ₹1 crore term cover for ₹8,000–12,000 per year. An endowment plan charging ₹50,000 per year gives 10x less coverage. The endowment premium is mostly going to agent commissions and investment charges."
      },
      {
        id: 2,
        question: "Why do agents push ULIPs and endowment plans so aggressively?",
        options: [
          "Because they genuinely perform better than mutual funds",
          "Because SEBI requires them to",
          "Because agents earn 20–40% commission on the first year's premium",
          "Because they are safer in a bear market"
        ],
        correct: 2,
        explanation: "Agent commissions on ULIPs and endowment plans can be 20–40% of your first year's premium. A mutual fund distributor earns far less. The product is designed around agent incentives — not your financial goals."
      },
      {
        id: 3,
        question: "What is the recommended approach to insurance and investing?",
        options: [
          "Buy a ULIP — it handles both together conveniently",
          "Buy term insurance separately for protection, and invest in mutual funds separately for growth",
          "Avoid insurance until you have enough savings",
          "Put everything in a fixed deposit"
        ],
        correct: 1,
        explanation: "'Buy term and invest the rest' is the standard advice from every serious financial planner. Term insurance is pure, cheap protection. Mutual funds are pure, efficient growth. Mixing them in one product gives you the worst of both."
      }
    ]
  },
  {
    id: 4,
    title: "Chasing last year's best fund",
    tag: "Investing mistake",
    duration: 5,
    description: "Last year's top-performing fund is often this year's worst. Buying mutual funds based on recent return rankings is one of the most expensive investing mistakes.",
    warning: "Top funds underperform the next year 70% of the time",
    content: {
      intro: "You open a mutual fund app. You sort by 'best 1-year returns.' You pick the fund at the top — it returned 45% last year. You invest. Over the next year it returns 6%. You switch to another top performer. The same thing happens again. This cycle quietly destroys wealth.",
      sections: [
        {
          heading: "Why past returns don't predict future ones",
          body: "When a fund performs well in a year, it usually means a specific sector or market condition favoured its strategy that year. Markets rotate. The fund that did well in infrastructure last year may do badly when those stocks fall. The fund itself hasn't changed — the market conditions that made it look good have."
        },
        {
          heading: "What the data shows",
          body: "SEBI's own data shows that fewer than 10% of actively managed large-cap funds consistently beat their benchmark over 10 years. The funds that beat it one year are not reliably the same ones that beat it the next. Returns rankings are a marketing tool — not a selection guide."
        },
        {
          heading: "Switching funds makes it worse",
          body: "Every time you move between funds you may pay an exit load (usually 1%) and trigger capital gains tax. If you hold for under a year, short-term capital gains tax applies. Frequent switching means you pay to get inferior returns."
        },
        {
          heading: "What to do instead",
          body: "Pick one or two broad index funds — Nifty 50 for large caps, Nifty Next 50 for mid-large caps. Don't look at rankings. Don't switch. Start a SIP and ignore the noise. The boring strategy consistently beats the exciting one over time."
        }
      ],
      example: "Vikram put ₹1 lakh each into the top 3 funds by 1-year return in 2018. All three underperformed the Nifty 50 index over the next 5 years. A simple index fund would have given him 60% more — with no research, no switching, and no stress.",
      keyTakeaway: "Past returns are a marketing number, not a prediction. Pick a diversified index fund and hold it for years. Switching funds based on rankings is a reliable way to earn below-market returns while paying extra fees to do it."
    },
    quiz: [
      {
        id: 1,
        question: "A mutual fund returned 45% last year — the best in its category. Is this a good reason to invest?",
        options: [
          "Yes — past performance is the best guide to future performance",
          "Yes — if it did well before, it will do well again",
          "No — past returns don't predict future performance and top funds often underperform next year",
          "Yes — but only invest half your money"
        ],
        correct: 2,
        explanation: "Top-performing funds in one year underperform in the next year roughly 70% of the time. High recent returns usually mean the fund is fully valued in those sectors. The factors that drove last year's returns may not repeat."
      },
      {
        id: 2,
        question: "You switch mutual funds every year to follow top performers. What is the hidden cost?",
        options: [
          "Nothing — switching is free",
          "Only a small ₹50 transaction fee",
          "Exit loads and capital gains tax that reduce your total returns",
          "A penalty from SEBI"
        ],
        correct: 2,
        explanation: "Exit loads (typically 1%) plus short-term capital gains tax (20%) on gains held under a year can significantly eat into returns. Frequent switchers often end up with much lower net returns than someone who stayed in a mediocre fund and didn't switch."
      },
      {
        id: 3,
        question: "Which approach consistently outperforms most actively managed funds over 10 years?",
        options: [
          "Picking the top 5 funds from the previous year",
          "Following tips from financial influencers",
          "Investing in a broad index fund like Nifty 50 and holding",
          "Switching between sectors based on news"
        ],
        correct: 2,
        explanation: "Index funds beat 80–90% of actively managed funds over 10 years, according to SPIVA reports. They have lower costs, no manager risk, and no style drift. The boring buy-and-hold approach wins."
      }
    ]
  },
  {
    id: 5,
    title: "Waiting for the 'right time' to invest",
    tag: "Investing mistake",
    duration: 5,
    description: "Every year people wait for markets to dip before investing. The cost of waiting almost always exceeds any timing advantage — and most people never actually invest.",
    warning: "Missing the 10 best market days in 20 years cuts returns by half",
    content: {
      intro: "'The market is at an all-time high — I'll wait for a correction.' 'The economy looks uncertain.' 'Let me wait for the budget.' These are sentences that have cost Indian investors lakhs every year. Waiting for the right time is one of the most expensive habits in personal finance.",
      sections: [
        {
          heading: "Why timing the market doesn't work",
          body: "No one — not fund managers, not economists, not analysts — can consistently predict short-term market movements. The rare person who calls it right once does so largely by luck. They rarely call it right twice. Studies across decades confirm this: professionals who time the market underperform those who don't."
        },
        {
          heading: "The cost of waiting",
          body: "Every year you sit in cash, inflation erodes your money and you miss market returns. If markets return 12% annually and you wait two years to invest, you need 30%+ returns just to catch up to where you would have been. Waiting is expensive — the cost compounds just like returns do."
        },
        {
          heading: "The 10 best days",
          body: "A famous analysis shows that if you missed just the 10 best trading days of the Nifty 50 over 20 years, your total returns would be cut roughly in half. You cannot know which 10 days those will be — they often happen during panics and recoveries. You have to be invested to capture them."
        },
        {
          heading: "SIP removes the need to time",
          body: "A Systematic Investment Plan (SIP) invests a fixed amount every month regardless of market levels. Some months you buy at high prices, some months at low prices. Over time it averages out — this is called rupee cost averaging. You stop worrying about timing because timing no longer matters."
        }
      ],
      example: "In March 2020, markets crashed 35% due to COVID. Many people said 'wait — it could fall more.' Those who started SIPs that month saw 80%+ returns by end of 2021. Those who waited for certainty missed most of the recovery and got in near the top.",
      keyTakeaway: "The best time to invest was yesterday. The second best time is today. Start a SIP with whatever you have, at whatever market level it is at. Time in the market always, consistently, reliably beats timing the market."
    },
    quiz: [
      {
        id: 1,
        question: "Markets are at an all-time high. Should you wait for a correction before starting your SIP?",
        options: [
          "Yes — always buy at the lowest possible price",
          "Yes — wait at least 6 months and see what happens",
          "No — markets reach all-time highs regularly; waiting costs you more than any dip saves",
          "Only invest 10% now and the rest later"
        ],
        correct: 2,
        explanation: "Markets make new all-time highs frequently — that is what long-term growth looks like. Historically, buying at market highs and holding still gives strong returns. The opportunity cost of waiting usually exceeds any benefit from buying at a slightly lower price."
      },
      {
        id: 2,
        question: "What is rupee cost averaging?",
        options: [
          "Converting all your money to rupees before investing",
          "Investing a fixed amount every month regardless of market level, averaging your purchase price over time",
          "Buying only when markets fall by 10% or more",
          "Dividing your investment equally between rupee and dollar assets"
        ],
        correct: 1,
        explanation: "Rupee cost averaging means you invest the same amount monthly no matter what markets are doing. When prices are high you buy fewer units. When prices are low you buy more. Over time your average cost smooths out — removing the need to time anything."
      },
      {
        id: 3,
        question: "You missed the 10 best trading days in the Nifty 50 over 20 years. What likely happened to your returns?",
        options: [
          "Returns stayed about the same — 10 days don't matter much",
          "Returns improved — avoiding volatile days is better",
          "Returns were cut roughly in half",
          "Returns increased because you avoided big losses"
        ],
        correct: 2,
        explanation: "The 10 best days often happen right after crashes, when most people are scared and sitting on the sidelines. Missing them can cut your 20-year returns in half. This is why staying invested through volatility beats trying to time entry and exit."
      }
    ]
  },
  {
    id: 6,
    title: "Not having term insurance",
    tag: "Protection mistake",
    duration: 5,
    description: "Most earning Indians have no life insurance, or have an endowment policy with ₹5–10 lakh cover. If you have dependants, this is a serious financial risk.",
    warning: "₹1 crore cover costs as little as ₹800/month for a 30-year-old",
    content: {
      intro: "If you earn money that your family depends on — and you die tomorrow — what happens to them? Can your spouse pay rent? Can your parents afford medical bills? Can your children continue school? For most Indian families, the honest answer is no. The fix costs less than a mobile recharge per day.",
      sections: [
        {
          heading: "What term insurance is",
          body: "Term insurance pays a large lump sum to your family if you die during the policy period. That is all it does. There is no 'maturity benefit' or 'money-back.' It is pure protection. Because it does one thing simply, it is very cheap — far cheaper than any policy that mixes investment with insurance."
        },
        {
          heading: "How cheap it actually is",
          body: "A ₹1 crore term policy for a healthy 30-year-old non-smoker costs ₹8,000–12,000 per year. That is ₹700–1,000 per month for your family to be financially secure if you die. Most people in urban India spend more than this on food delivery in a month."
        },
        {
          heading: "Why people avoid it",
          body: "People feel term insurance is 'wasteful' because they get nothing back if they don't die. This thinking is what makes endowment plans sell — but as we've seen, those give poor protection and poor returns. The 'waste' of term insurance is its entire point: you pay a small amount hoping never to use it."
        },
        {
          heading: "Who needs it and how much",
          body: "If anyone depends on your income — spouse, children, parents, siblings — you need term insurance. If you're single with no dependants, it is optional. The standard recommendation is coverage of 10–15 times your annual income. On a ₹6 lakh annual income, aim for ₹60–90 lakh minimum cover."
        }
      ],
      example: "Suresh, 34, earned ₹8 lakh per year and had only a ₹5 lakh LIC endowment policy. He died in an accident. His wife used the ₹5 lakh to pay off existing debts. They had two school-age children. A ₹1 crore term policy at ₹12,000 per year would have secured his family financially for years.",
      keyTakeaway: "If someone depends on your income, buy term insurance today. ₹1 crore cover, 30-year term. Trusted providers include HDFC Life, ICICI Prudential, and Max Life. It costs less than you think and protects more than anything else you can buy."
    },
    quiz: [
      {
        id: 1,
        question: "What is the key difference between term insurance and an endowment plan?",
        options: [
          "Term insurance is only for the elderly",
          "Endowment plans are cheaper",
          "Term insurance is pure protection with no maturity payout — that is why it is much cheaper",
          "Term insurance also gives returns on maturity"
        ],
        correct: 2,
        explanation: "Term insurance does one thing: pays your family if you die. No investment, no maturity benefit, no complexity. Because it is simple and pure, premiums are very low — making it the most efficient way to protect your family."
      },
      {
        id: 2,
        question: "You earn ₹6 lakh per year. How much term insurance cover should you aim for?",
        options: [
          "₹5–10 lakh — just to cover immediate expenses",
          "₹20–30 lakh",
          "₹60–90 lakh (10–15x annual income)",
          "₹5 crore — as much as possible"
        ],
        correct: 2,
        explanation: "The standard recommendation is 10–15 times your annual income. This ensures your family can invest the sum and live off returns without depleting the principal. On ₹6 lakh income, ₹60–90 lakh cover is appropriate."
      },
      {
        id: 3,
        question: "Who needs term insurance?",
        options: [
          "Only people over 40",
          "Only the primary earner in a family",
          "Anyone whose income dependants rely on, regardless of age",
          "No one — savings are enough"
        ],
        correct: 2,
        explanation: "If your parents, spouse, or children depend on your income — you need term insurance. The younger you buy it, the cheaper it is. A 25-year-old pays significantly less than a 35-year-old for the same cover."
      }
    ]
  }
];
